export function withFallback(value: unknown): string {
    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "") ||
      (typeof value === "number" && isNaN(value))
    ) {
      return "-";
    }
  
    return String(value);
  }
  
  export function formatDate(date: string | number | Date | null | undefined): string {
    try {
      if (!date) return "-";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  }

  /**
   * Format a contract boundary (the subscription `renewDate`) as dd/mm/yyyy.
   *
   * Separate from `formatDate` because it renders in UTC, and only contract
   * dates should. A contract boundary is a calendar day, not an instant: the
   * term renews on the 4th for everyone, whether they read the page from Mexico
   * City or Madrid. `formatDate` renders in the viewer's zone, which is right
   * for a timestamp — when something happened is genuinely local — and wrong
   * here: a date the backend sends as midnight UTC renders as the 3rd for every
   * browser west of UTC, showing the whole customer base in the Americas a
   * renewal a day early (CTX-434).
   *
   * The bug is latent today only by accident. Chargebee anchors
   * `contract_start` at 06:00 UTC, midnight in America/Mexico_City, and that
   * six-hour offset happens to cancel out. The backfill normalizes the stored
   * `contractStart` to midnight UTC, which removes the anchor and exposes it.
   */
  export function formatContractDate(
    date: string | number | Date | null | undefined,
  ): string {
    try {
      if (!date) return "-";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      });
    } catch {
      return "-";
    }
  }
  
  export function formatCamelCase(text: string): string {
    return text
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  }

  export function formatTerm(term: unknown, unit: unknown): string {
    const t = withFallback(term);
    const u = withFallback(unit);
  
    // If both are fallback dashes, just return one
    if (t === "-" && u === "-") return "-";
  
    // If only one has value, return just that one
    if (t === "-") return u;
    if (u === "-") return t;
  
    // If both exist
    return `${t} ${u}`;
  }

  export const formatLabelWithParentheses = (main?: string, sub?: string): string => {
    const mainSafe = withFallback(main);
    const subSafe = withFallback(sub);
  
    if (mainSafe === "-" && subSafe === "-") return "-";
    if (mainSafe === "-") return `(${subSafe})`;
    if (subSafe === "-") return mainSafe;
    return `${mainSafe} (${subSafe})`;
  };
  
  /** Formats to: DD/MM/YYYY , HH:MM GMT-6 (24h), using the given TZ (defaults MX) */
  export function formatDateTimeTZ(
    input: string | number | Date | null | undefined,
    opts?: {
      locale?: string;                 // default: 'es-MX'
      timeZone?: string;               // default: 'America/Mexico_City'
      includeSeconds?: boolean;        // default: false
      separator?: string;              // default: " , "
    }
  ): string {
    try {
      if (!input) return "-";
      const d = new Date(input);
      if (isNaN(d.getTime())) return "-";

      const {
        locale = "es-MX",
        timeZone = "America/Mexico_City",
        includeSeconds = false,
        separator = " , ",
      } = opts ?? {};

      // Date parts in dd/mm/yyyy
      const dateStr = d.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone,
      });

      // Time (24h) and offset using shortOffset if supported (e.g., "GMT-06:00")
      const timeFmt = new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        second: includeSeconds ? "2-digit" : undefined,
        hour12: false,
        timeZone,
        timeZoneName: "shortOffset", // modern engines -> "GMT-06:00"
      });

      const parts = timeFmt.formatToParts(d);
      const hh = parts.find(p => p.type === "hour")?.value ?? "--";
      const mm = parts.find(p => p.type === "minute")?.value ?? "--";
      const ss = includeSeconds ? (parts.find(p => p.type === "second")?.value ?? "--") : null;
      let tzName = parts.find(p => p.type === "timeZoneName")?.value ?? "";

      // Normalize "GMT-06:00" -> "GMT-6" (keep minutes if non-zero)
      tzName = tzName.replace(/^GMT([+-])0?(\d+):00$/, "GMT$1$2");
      // Special-case UTC to display "UTC" instead of "GMT"
      if ((opts?.timeZone ?? "America/Mexico_City").toUpperCase() === "UTC") {
        tzName = "UTC";
      } else if (/^GMT(\+0|-0|)$/.test(tzName)) {
        //Catch plain "GMT" or zero-offset variants
        tzName = "UTC";
      }

      

      const timeStr = includeSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
      return `${dateStr}${separator}${timeStr} ${tzName || ""}`.trim();
    } catch {
      return "-";
    }
  }


  /*
   * `setStatusTag` lived here and is gone. It was a fifth copy of the status
   * mapping — after the four CTX-547 collapsed into `resolveServiceStatusTag`
   * — and it carried the same defect they did: an absent status and the
   * literal `'unknown'` the backend sends when it could not read the telemetry
   * both came back amber "Unknown", which asserts a problem from the absence
   * of evidence. Its only caller was `toHistoryRows`, so the history table of
   * a Cloud Connectivity detail painted a collector outage amber while the
   * list one click away painted it grey. Same value, same screen, two answers.
   *
   * It is deleted rather than made to delegate: a pass-through is a name that
   * invites the rule to be re-implemented behind it. Callers import
   * `resolveServiceStatusTag` from `helpers/serviceStatusTag` directly.
   */
